'use client';

import type { UnpackedUserToken } from '@/app_actions/Checkout/entities';
import { CookieService } from '@/app_lib/CookieService';
import { checkResourceAccess } from '@/app_lib/services/tinypass/actions';
import logApiError from '@/main/lib/utils/log/logApiError';

export const unpackAndVerifyUserTokenClient = async (token: string) => {
  const response = await fetch('/api/verifyToken', {
    body: JSON.stringify({ token }),
    headers: {
      'Content-Type': 'application/json',
    },
    method: 'POST',
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Token verification failed: ${errorText}`);
  }

  const result = await response.json();

  if (result.error) {
    throw new Error(`Token verification error: ${result.error}`);
  }

  return result.decoded as UnpackedUserToken;
};

export const getIsLoggedInClient = async (): Promise<{
  data: null | UnpackedUserToken;
  pianoToken: string;
}> => {
  const pianoToken = CookieService.get('__utp');

  if (pianoToken) {
    try {
      const verifiedToken = await unpackAndVerifyUserTokenClient(pianoToken);
      if (verifiedToken.sub) {
        return { data: verifiedToken, pianoToken };
      }
      throw new Error('User token is invalid or missing sub');
    } catch (err) {
      // invalid token
      logApiError(err as Error, '[getIsLoggedIn] Invalid user token');
      return { data: null, pianoToken: '' };
    }
  }
  return { data: null, pianoToken: '' };
};

export const getUserAccessClient = async () => {
  try {
    let hasAccess = false;
    const { data: verifiedToken, pianoToken } = await getIsLoggedInClient();

    if (verifiedToken && pianoToken) {
      const accessResponse = await checkResourceAccess(verifiedToken.sub);
      hasAccess = Boolean(accessResponse?.access?.granted);
      return { data: verifiedToken, hasAccess, loggedIn: Boolean(verifiedToken.sub), pianoUserToken: pianoToken };
    }
  } catch (err) {
    // invalid token
    logApiError(err as Error, '[getIsLoggedIn] Invalid user token');
  }
  return { data: null, hasAccess: false, loggedIn: false, pianoUserToken: '' };
};
